<template>
  <div
    class="doctor-avatar-editor"
    data-qa="doctor-avatar-editor"
  >
    <div
      class="doctor-avatar-editor__activator"
      @click="onPickClick"
    >
      <slot />
    </div>
    <input
      ref="fileInputRef"
      class="d-none"
      type="file"
      accept="image/jpeg,image/png,image/webp"
      @change="onFileChange"
    >

    <DoctorAvatarEditorModal
      :value="isPreviewOpen"
      :title="t('Фотография профиля', l10nOptions)"
      :is-loading="isLoading"
      :image-src="previewSrc"
      :is-staff="isStaff"
      :is-upscaling-enabled="isUpscalingEnabled"
      :is-init-upscale-photo="isUpscaleImage"
      @cancel="closePreview"
      @submit="saveAvatar"
      @rotate-clockwise="rotateBy(ROTATION_ANGLE_PER_STEP)"
      @rotate-counter-clockwise="rotateBy(-ROTATION_ANGLE_PER_STEP)"
      @switch-crop-photo="onSwitchCropPhoto"
      @switch-upscale-photo="onSwitchUpscalePhoto"
    />

    <CoreDialog
      v-model="isAlertOpen"
      data-qa="doctor-avatar-editor-alert"
      size="sm"
      :title="alertTitle"
    >
      <div
        v-if="alertText"
        class="text-body-1 text-secondary--text"
      >
        {{ alertText }}
      </div>
      <template #actions>
        <v-btn
          color="text"
          text
          @click="closeAlert"
        >
          {{ t('Закрыть', l10nOptions) }}
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="retryPick"
        >
          {{ t('Выбрать фото', l10nOptions) }}
        </v-btn>
      </template>
    </CoreDialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import i18next from 'i18next'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import type { CommonContextStore } from 'src/types/context'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import apiDoctorAvatar from 'src/components/common/DoctorAvatarEditor/api/apiDoctorAvatar'
import DoctorAvatarEditorModal from 'src/components/common/DoctorAvatarEditor/components/DoctorAvatarEditorModal.vue'
import {
  AVATAR_ERROR_MESSAGES,
  AVATAR_ERROR_TYPE,
  INIT_ROTATION_ANGLE,
  MAX_ROTATION_ANGLE,
  ROTATION_ANGLE_PER_STEP,
} from 'src/components/common/DoctorAvatarEditor/constants'
import {
  getAvatarFileErrorType,
  getAvatarImageErrorType,
  readFileAsDataUrl,
} from 'src/components/common/DoctorAvatarEditor/functions/avatarFile'
import getAvatarEditorError from 'src/components/common/DoctorAvatarEditor/functions/getAvatarEditorError'
import mapProcessedAvatar, {
  getProcessedAvatarPreview,
  type ProcessedAvatar,
} from 'src/components/common/DoctorAvatarEditor/functions/mapProcessedAvatar'

const { t, language } = i18next
const l10nOptions = { ns: language }

const props = defineProps<{
  doctorId: number
  imageSelector?: string
  lazyRefresh?: boolean
  useOriginalImage?: boolean
}>()

const { context }: CommonContextStore = useContextStore()
const { reverse } = useURLStore()
const isStaff = computed(() => Boolean(context.request.user.is_staff))
const fileInputRef = ref<HTMLInputElement | null>(null)
const isPreviewOpen = ref(false)
const isAlertOpen = ref(false)
const isLoading = ref(false)
const alertTitle = ref('')
const alertText = ref('')
const originalBase64 = ref('')
const rotation = ref(INIT_ROTATION_ANGLE)
const isCropImage = ref(true)
const isUpscaleImage = ref(true)
const isUpscalingEnabled = ref(false)
const processedByRotation = ref<Record<number, ProcessedAvatar>>({})

const previewSrc = computed(() => getProcessedAvatarPreview(
  processedByRotation.value[rotation.value],
  isCropImage.value,
  isUpscaleImage.value,
  isUpscalingEnabled.value,
))

const currentProcessed = computed(() => processedByRotation.value[rotation.value])

const apiUrls = computed(() => {
  const processAvatar = reverse({ name: 'profile_doctors_ajax:process_avatar' })
    || '/profile/doctors/process-avatar/'
  const saveAvatarUrl = reverse({ name: 'profile_doctors_ajax:save_avatar' })
    || '/profile/doctors/save-avatar/'

  return {
    processAvatar,
    saveAvatar: saveAvatarUrl,
  }
})

function showAlert(title: string, text = '') {
  alertTitle.value = title
  alertText.value = text
  isAlertOpen.value = true
}

function showTypedAlert(type: string) {
  const message = AVATAR_ERROR_MESSAGES[type] || AVATAR_ERROR_MESSAGES[AVATAR_ERROR_TYPE.other]
  const text = message.text ? t(message.text, l10nOptions) : ''

  showAlert(t(message.title, l10nOptions), text)
}

function closeAlert() {
  isAlertOpen.value = false
}

function retryPick() {
  closeAlert()
  fileInputRef.value?.click()
}

function onSwitchCropPhoto(isEnabled: boolean) {
  isCropImage.value = isEnabled
}

function onSwitchUpscalePhoto(isEnabled: boolean) {
  isUpscaleImage.value = isEnabled
}

function closePreview() {
  isPreviewOpen.value = false
  isLoading.value = false
  originalBase64.value = ''
  rotation.value = INIT_ROTATION_ANGLE
  isCropImage.value = true
  isUpscaleImage.value = true
  isUpscalingEnabled.value = false
  processedByRotation.value = {}
}

function refreshImageSrc() {
  const imageSelector = props.imageSelector || ''
  const lazyRefresh = props.lazyRefresh !== false
  const useOriginalImage = Boolean(props.useOriginalImage)

  if (!lazyRefresh && process.env.IS_CLIENT) {
    window.location.reload()

    return
  }

  if (!imageSelector || !process.env.IS_CLIENT) {
    return
  }

  const image = document.querySelector<HTMLImageElement>(imageSelector)

  if (image) {
    image.src = useOriginalImage ? originalBase64.value : previewSrc.value
  }
}

function onPickClick() {
  if (!process.env.IS_CLIENT) {
    return
  }

  fileInputRef.value?.click()
}

async function processCurrentImage() {
  isLoading.value = true

  try {
    const processedList = await apiDoctorAvatar.process({
      image: originalBase64.value,
      doctor_id: props.doctorId,
      rotation: rotation.value,
    }, apiUrls.value)

    if (!processedList.length) {
      throw new Error('empty process response')
    }

    isUpscalingEnabled.value = Boolean(processedList[0].upscale_crop_coords)
    processedByRotation.value = processedList.reduce((result, item) => ({
      ...result,
      [item.rotation]: mapProcessedAvatar(item),
    }), processedByRotation.value)
  } catch (error) {
    const message = getAvatarEditorError(error)
    const text = message.text ? t(message.text, l10nOptions) : ''

    closePreview()
    showAlert(t(message.title, l10nOptions), text)
  } finally {
    isLoading.value = false
  }
}

async function rotateBy(step: number) {
  rotation.value = (rotation.value + step + MAX_ROTATION_ANGLE) % MAX_ROTATION_ANGLE

  if (processedByRotation.value[rotation.value]) {
    return
  }

  await processCurrentImage()
}

async function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]

  input.value = ''

  if (!file) {
    return
  }

  const fileErrorType = getAvatarFileErrorType(file)

  if (fileErrorType) {
    showTypedAlert(fileErrorType)

    return
  }

  try {
    const dataUrl = await readFileAsDataUrl(file)
    const imageErrorType = await getAvatarImageErrorType(dataUrl)

    if (imageErrorType) {
      showTypedAlert(imageErrorType)

      return
    }

    originalBase64.value = dataUrl
    rotation.value = INIT_ROTATION_ANGLE
    processedByRotation.value = {}
    isCropImage.value = true
    isUpscaleImage.value = true
    isPreviewOpen.value = true
    await processCurrentImage()
  } catch {
    closePreview()
    showTypedAlert(AVATAR_ERROR_TYPE.uploadImage)
  }
}

async function saveAvatar() {
  if (!originalBase64.value || !previewSrc.value || !currentProcessed.value) {
    return
  }

  isLoading.value = true

  try {
    await apiDoctorAvatar.save({
      source_image: originalBase64.value,
      processed_image: previewSrc.value,
      crop_coords: currentProcessed.value.cropCoords,
      rotation: currentProcessed.value.rotationAngle,
      doctor_id: props.doctorId,
    }, apiUrls.value)
    refreshImageSrc()
    closePreview()
  } catch (error) {
    const message = getAvatarEditorError(error)
    const text = message.text ? t(message.text, l10nOptions) : ''

    closePreview()
    showAlert(t(message.title, l10nOptions), text)
  } finally {
    isLoading.value = false
  }
}
</script>
